import { TpTemplateInfo } from '../../../../api/models/assets';
import {
  FORM_DATATYPES,
  TpAutoFormChoice,
  TpAutoFormChoiceIcon,
  TpAutoFormDef,
} from '../../../../components/auto-form-modal/AutoForm';
import { formatDate } from '../../../../components/miscUtils';

export interface SelectTemplateFormOptions {
  templates: TpTemplateInfo[];
  currentTemplateId?: string;
  onSelectTemplate: (selectedTemplate: TpTemplateInfo) => Promise<void> | void;
  onRevertDefault?: () => Promise<void> | void;
  close: () => void;
}

/**
 * Creates the AutoForm modal definition for selecting a dashboard template.
 */
export function selectTemplateForm({
  templates,
  currentTemplateId,
  onSelectTemplate,
  onRevertDefault,
  close,
}: SelectTemplateFormOptions): TpAutoFormDef {
  const choices: TpAutoFormChoice[] = templates.map((template) => {
    const tooltipLines: string[] = [`Name: ${template.displayName || template.name}`];
    if (template.version) {
      tooltipLines.push(`Version: ${template.version}`);
    }
    if (template.updatedAt) {
      tooltipLines.push(`Updated at: ${formatDate(template.updatedAt)}`);
    }

    const icons: TpAutoFormChoiceIcon[] = [];
    if (template.isDefault) {
      icons.push({ iconName: 'star', iconPrefix: 'fas', title: 'Default' });
    }
    if (template.isActive === false) {
      icons.push({ iconName: 'eye-slash', iconPrefix: 'fas', title: 'Inactive' });
    }

    return {
      id: template.id || '',
      content: template.displayName || template.name,
      title: tooltipLines.length > 0 ? tooltipLines.join('\n') : undefined,
      icons,
    };
  });

  const matchingChoice = choices.find((c) => c.id === currentTemplateId);
  const initialChoice = matchingChoice ? matchingChoice.id : choices[0]?.id || '';

  const form: TpAutoFormDef = {
    title: 'Select Dashboard',
    fields: [
      {
        id: 'templateId',
        name: 'Template',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        choices,
        startContent: initialChoice,
        required: true,
      },
    ],
    actionButtons: [
      {
        id: 'cancel',
        content: 'Cancel',
      },
      {
        id: 'revertDefault',
        content: 'Revert to Default',
        isSecondary: true,
      },
      {
        id: 'select',
        content: 'Load',
      },
    ],
    submit: async (formData) => {
      const actionButtonId = formData.actionButtonId;
      if (actionButtonId === 'revertDefault') {
        if (onRevertDefault) {
          await onRevertDefault();
        }
        close();
        return;
      }

      const selectedId = formData.templateId;
      const selected = templates.find((t) => t.id === selectedId);
      if (selected) {
        await onSelectTemplate(selected);
      }
      close();
    },
  };

  return form;
}
